import { Button, InputText, Select, Tooltip } from "@/deps/ui-library";
import type { BillingItem, VatCategory } from "@/api/types";
import { lineNetAmount, type LineInput } from "@/lib/invoiceTotals";
import { cn } from "@/lib/cn";
import { formatAmount } from "@/lib/money";
import { useTaxWords } from "@/app/session";

export interface EditableLine extends LineInput {
  id: string;
  description: string;
  accountId: string;
  /** The HS code: copied from a billing item, or typed where the company shows the column. */
  itemCode?: string;
  exemptionReason?: string;
  billingItemId?: string;
}

/** A line filled from a billing item. It's a copy, which can be changed like any other line. */
export function lineFromItem(item: BillingItem, id: string): EditableLine {
  return {
    id,
    description: item.description ? `${item.name} · ${item.description}` : item.name,
    accountId: item.incomeAccountId,
    category: item.category,
    rate: item.category === "O" ? null : (item.ratePct ?? "0"),
    grossPrice: item.unitPrice,
    quantity: "1",
    ...(item.hsCode ? { itemCode: item.hsCode } : {}),
    ...(item.exemptionReason ? { exemptionReason: item.exemptionReason } : {}),
    billingItemId: item.id,
  };
}

export interface AccountOption {
  id: string;
  code: string;
  name: string;
}

const CATEGORIES: Array<{ value: VatCategory; label: string; hint: string }> = [
  { value: "S", label: "Standard rate", hint: "VAT is charged at the rate shown." },
  {
    value: "Z",
    label: "Zero-rated",
    hint: "0%, and input tax on related purchases is still recoverable.",
  },
  {
    value: "E",
    label: "Exempt",
    hint: "No VAT, and input tax on related purchases is not recoverable.",
  },
  { value: "AE", label: "Reverse charge", hint: "The buyer accounts for the tax." },
  { value: "O", label: "Outside scope", hint: "No rate at all, and no tax." },
];

export const CATEGORY_OPTIONS = CATEGORIES.map(({ value, label }) => ({ value, label }));

/** An issued document renders the same grid read-only: the inputs lose their box, not their text. */
const READ_ONLY =
  "read-only:border-transparent read-only:bg-transparent read-only:hover:bg-transparent";

/**
 * The editable grid of invoice lines.
 *
 * Two things here are not cosmetic:
 *
 * **Price and quantity are not amount fields.** BIS puts no decimal restriction on a unit price or
 * a quantity, and two decimals on an amount. A single "money" input mask everywhere silently
 * truncates a price of `10000.1234`, which is a real price for a per-unit service.
 *
 * **The running total is the same calculation the server will do.** `lib/invoiceTotals.ts` and the
 * C# `InvoiceCalculator` are tested against the same vectors, so the figure under the lines is the
 * figure that will be issued. A total that changes when you press Save is what that prevents.
 */
export function LineEditor({
  lines,
  onChange,
  accounts,
  readOnly = false,
  items,
  showHsCode = false,
  lineDiscounts = false,
}: {
  lines: EditableLine[];
  onChange: (lines: EditableLine[]) => void;
  accounts: AccountOption[];
  readOnly?: boolean;
  /** Offered as a way to add a line, on documents that sell something. */
  items?: BillingItem[];
  showHsCode?: boolean;
  /** A discount and a charge on each line. */
  lineDiscounts?: boolean;
}) {
  const say = useTaxWords();
  const update = (id: string, patch: Partial<EditableLine>) =>
    onChange(lines.map((line) => (line.id === id ? { ...line, ...patch } : line)));

  const accountOptions = accounts.map((account) => ({
    value: account.id,
    label: `${account.code} · ${account.name}`,
  }));

  const remove = (id: string) => onChange(lines.filter((line) => line.id !== id));

  // A first line nobody has started on is what the form opens with, so an item takes its place.
  const addItem = (item: BillingItem) => {
    const added = lineFromItem(item, crypto.randomUUID());
    const untouched = (line: EditableLine) =>
      line.description.trim() === "" && line.grossPrice.trim() === "";
    onChange([...lines.filter((line) => !untouched(line)), added]);
  };

  const append = () =>
    onChange([
      ...lines,
      {
        id: crypto.randomUUID(),
        description: "",
        accountId: accounts[0]?.id ?? "",
        category: "S",
        rate: "13",
        grossPrice: "",
        quantity: "1",
      },
    ]);

  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">Invoice lines</caption>
          <thead>
            <tr className="border-border border-b">
              <th
                scope="col"
                className="text-foreground min-w-80 px-2 py-1.5 text-left text-xs font-medium"
              >
                Description
              </th>
              {showHsCode ? (
                <th
                  scope="col"
                  className="text-foreground px-2 py-1.5 text-left text-xs font-medium"
                >
                  HS code
                </th>
              ) : null}
              <th scope="col" className="text-foreground px-2 py-1.5 text-left text-xs font-medium">
                Account
              </th>
              <th
                scope="col"
                className="text-foreground px-2 py-1.5 text-right text-xs font-medium"
              >
                Quantity
              </th>
              <th
                scope="col"
                className="text-foreground px-2 py-1.5 text-right text-xs font-medium"
              >
                Unit price
              </th>
              {lineDiscounts ? (
                <>
                  <th
                    scope="col"
                    className="text-foreground px-2 py-1.5 text-right text-xs font-medium"
                  >
                    Discount
                  </th>
                  <th
                    scope="col"
                    className="text-foreground px-2 py-1.5 text-right text-xs font-medium"
                  >
                    Charge
                  </th>
                </>
              ) : null}
              <th scope="col" className="text-foreground px-2 py-1.5 text-left text-xs font-medium">
                {say("VAT")}
              </th>
              <th
                scope="col"
                className="text-foreground px-2 py-1.5 text-right text-xs font-medium"
              >
                Rate
              </th>
              <th
                scope="col"
                className="text-foreground px-2 py-1.5 text-right text-xs font-medium"
              >
                Line amount
              </th>
              {readOnly ? null : (
                <th scope="col" className="w-8">
                  <span className="sr-only">Remove</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const net = lineNetAmount(line);
              const category = CATEGORIES.find((c) => c.value === line.category);

              return (
                <tr key={line.id} className="border-muted border-b align-top last:border-0">
                  <td className="px-2 py-1">
                    <InputText.Input
                      value={line.description}
                      onChange={(event) => update(line.id, { description: event.target.value })}
                      readOnly={readOnly}
                      aria-label={`Line ${index + 1} description`}
                      config={{ size: "sm" }}
                      className={READ_ONLY}
                    />
                  </td>
                  {showHsCode ? (
                    <td className="px-2 py-1">
                      <InputText.Input
                        value={line.itemCode ?? ""}
                        onChange={(event) => update(line.id, { itemCode: event.target.value })}
                        readOnly={readOnly}
                        aria-label={`Line ${index + 1} HS code`}
                        config={{ size: "sm" }}
                        className={cn("num w-24", READ_ONLY)}
                      />
                    </td>
                  ) : null}
                  <td className="px-2 py-1">
                    <CellSelect
                      label={`Line ${index + 1} account`}
                      value={line.accountId}
                      disabled={readOnly}
                      options={accountOptions}
                      onChange={(accountId) => update(line.id, { accountId })}
                    />
                  </td>
                  {/* Quantity and price carry unrestricted decimals; the amount column carries two.
                      They are deliberately not the same control. */}
                  <NumberCell
                    label={`Line ${index + 1} quantity`}
                    value={line.quantity}
                    readOnly={readOnly}
                    onChange={(value) => update(line.id, { quantity: value })}
                  />
                  <NumberCell
                    label={`Line ${index + 1} unit price`}
                    value={line.grossPrice}
                    readOnly={readOnly}
                    onChange={(value) => update(line.id, { grossPrice: value })}
                  />
                  {lineDiscounts ? (
                    <>
                      <NumberCell
                        label={`Line ${index + 1} discount`}
                        value={line.allowances ?? ""}
                        readOnly={readOnly}
                        onChange={(value) => update(line.id, { allowances: value })}
                      />
                      <NumberCell
                        label={`Line ${index + 1} charge`}
                        value={line.charges ?? ""}
                        readOnly={readOnly}
                        onChange={(value) => update(line.id, { charges: value })}
                      />
                    </>
                  ) : null}
                  <td className="px-2 py-1">
                    {/* The hint is what tells zero-rated from exempt, which people conflate. */}
                    <Tooltip
                      content={say(category?.hint ?? "")}
                      disabled={!category}
                      placement="top"
                    >
                      <div>
                        <CellSelect
                          label={say(`Line ${index + 1} VAT category`)}
                          value={line.category}
                          disabled={readOnly}
                          options={CATEGORY_OPTIONS}
                          onChange={(value) => {
                            const next = value as VatCategory;
                            // Category O carries no rate at all. Leaving the old rate behind would
                            // be refused at issue, and the refusal would arrive long after the
                            // choice.
                            update(line.id, {
                              category: next,
                              rate: next === "O" ? null : (line.rate ?? "0"),
                            });
                          }}
                        />
                      </div>
                    </Tooltip>
                  </td>
                  <td className="px-2 py-1 text-right">
                    {line.category === "O" ? (
                      <span className="text-muted-foreground num text-xs">—</span>
                    ) : (
                      <InputText.Input
                        value={line.rate ?? ""}
                        onChange={(event) => update(line.id, { rate: event.target.value })}
                        readOnly={readOnly}
                        inputMode="decimal"
                        aria-label={say(`Line ${index + 1} VAT rate`)}
                        config={{ size: "sm" }}
                        className={cn("num ml-auto w-16 text-right", READ_ONLY)}
                      />
                    )}
                  </td>
                  <td className="num text-foreground px-2 py-2.5 text-right">
                    {/* A zero price base quantity is refused rather than divided by, so the cell
                        says so instead of showing NaN. */}
                    {net === null ? (
                      <span className="text-destructive text-xs">Check quantity</span>
                    ) : (
                      formatAmount(net)
                    )}
                  </td>
                  {readOnly ? null : (
                    <td className="px-1 py-1">
                      <Button.IconButton
                        icon="Trash2"
                        variant="ghost"
                        colorScheme="default"
                        size="sm"
                        onClick={() => remove(line.id)}
                        aria-label={`Remove line ${index + 1}`}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {readOnly ? null : (
        <div className="flex flex-wrap items-center gap-2">
          <Button colorScheme="default" variant="outline" size="sm" onClick={append}>
            Add line
          </Button>
          {items && items.length > 0 ? (
            <div className="min-w-64">
              <Select
                label="Add a billing item"
                hideLabel
                placeholder="Add a billing item"
                value=""
                options={items.map((item) => ({
                  value: item.id,
                  label: `${item.name} · ${formatAmount(item.unitPrice)}`,
                }))}
                showClearButton={false}
                onChange={(details) => {
                  const chosen = Array.isArray(details.value) ? details.value[0] : details.value;
                  const item = items.find((i) => i.id === String(chosen?.value));
                  if (item) addItem(item);
                }}
              />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function NumberCell({
  label,
  value,
  onChange,
  readOnly,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  readOnly: boolean;
}) {
  return (
    <td className="px-2 py-1">
      <InputText.Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        readOnly={readOnly}
        // `inputMode` rather than `type="number"` — or RDS NumberInput, which parses the same way:
        // a number input silently discards what it cannot parse mid-keystroke, so "12." disappears
        // as somebody types "12.5", and its spinners change the value on scroll.
        inputMode="decimal"
        aria-label={label}
        config={{ size: "sm" }}
        className={cn("num ml-auto w-24 text-right", READ_ONLY)}
      />
    </td>
  );
}

/**
 * An RDS `Select` in a grid cell. The label is the cell's accessible name — "Line 2 account" — and
 * is kept off the screen: the column header already says it to anyone looking.
 */
function CellSelect({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="min-w-32">
      <Select
        label={label}
        hideLabel
        value={value}
        options={options}
        disabled={disabled}
        showClearButton={false}
        placeholder="Choose…"
        onChange={(details) => {
          const chosen = Array.isArray(details.value) ? details.value[0] : details.value;
          if (chosen) onChange(String(chosen.value));
        }}
      />
    </div>
  );
}
